import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ThemeService } from '../../../services/theme/theme.service';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';

const NAVBAR_DROPDOWN_OPEN = 'navbar-dropdown-open';

type AppsLauncherItem = {
  label: string;
  route: string;
  /** Local Flaticon-style image under assets/icons/more-searches. */
  image: string;
  shade: string;
  permissionKey?: string;
  /** When true, show unless permission is explicitly false. */
  defaultAllowed?: boolean;
};

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, NotificationBellComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements OnInit, OnDestroy {
  subscription: any = {};
  isAppsOpen = false;
  private readonly onNavbarDropdownOpenBound = (event: Event) => {
    const detail = (event as CustomEvent<string>).detail;
    if (detail && detail !== 'apps') {
      this.closeAppsMenu();
    }
  };

  readonly appsLauncherItems: AppsLauncherItem[] = [
    {
      label: 'PAN Search',
      route: '/pan-search',
      image: 'assets/icons/more-searches/pan.png',
      shade: 'shade-green',
      permissionKey: 'pan_search_permission',
    },
    {
      label: 'Fastag\nSearch',
      route: '/phonepe-fastag-search',
      image: 'assets/icons/more-searches/fastag.png',
      shade: 'shade-orange',
      permissionKey: 'phonepe_fastag_search_permission',
    },
    {
      label: 'Vehicle\nSearch',
      route: '/vehicle-search',
      image: 'assets/icons/more-searches/vehicle.png',
      shade: 'shade-lime',
      permissionKey: 'vehicle_search_permission',
    },
    {
      label: 'Virtual Number',
      route: '/virtual-number',
      image: 'assets/icons/more-searches/virtual-number.png',
      shade: 'shade-pink',
      permissionKey: 'virtual_number_permission',
    },
    {
      label: 'Web Capture',
      route: '/web-capture',
      image: 'assets/icons/more-searches/web-capture.png',
      shade: 'shade-indigo',
      permissionKey: 'web_capture_permission',
    },
    {
      label: 'Advanced Search',
      route: '/advanced-search',
      image: 'assets/icons/more-searches/advanced-search.png',
      shade: 'shade-teal',
      permissionKey: 'name_search_permission',
    },
  ];

  constructor(
    public router: Router,
    private themeService: ThemeService,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit() {
    this.retrieveSubscriptionDetails();
    window.addEventListener(NAVBAR_DROPDOWN_OPEN, this.onNavbarDropdownOpenBound);
  }

  ngOnDestroy(): void {
    window.removeEventListener(NAVBAR_DROPDOWN_OPEN, this.onNavbarDropdownOpenBound);
  }

  get visibleApps(): AppsLauncherItem[] {
    return this.appsLauncherItems.filter((item) => this.canShowApp(item));
  }

  retrieveSubscriptionDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    if (USER_PORTAL) {
      const userPortal = JSON.parse(USER_PORTAL);
      this.subscription = userPortal?.subscriptionDetails || {};
    }
  }

  canShowApp(item: AppsLauncherItem): boolean {
    if (!item.permissionKey) {
      return item.defaultAllowed !== false;
    }
    return !!this.subscription?.[item.permissionKey];
  }

  toggleAppsMenu(event?: Event): void {
    event?.stopPropagation();
    if (this.isAppsOpen) {
      this.closeAppsMenu();
      return;
    }
    this.isAppsOpen = true;
    window.dispatchEvent(
      new CustomEvent(NAVBAR_DROPDOWN_OPEN, { detail: 'apps' }),
    );
  }

  closeAppsMenu(): void {
    this.isAppsOpen = false;
  }

  onAppNavigate(): void {
    this.closeAppsMenu();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isAppsOpen) {
      return;
    }
    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) {
      const launcher = this.host.nativeElement.querySelector('.apps-launcher');
      if (launcher?.contains(target)) {
        return;
      }
    }
    this.closeAppsMenu();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeAppsMenu();
  }

  isActive(keyword: string): boolean {
    return this.router.url.includes(keyword);
  }

  isHomeActive(): boolean {
    return this.router.url.split('?')[0] === '/home';
  }

  isOsintSearchActive(): boolean {
    const path = this.router.url.split('?')[0];
    return path.includes('osint') && !path.includes('osint-image-search');
  }

  goToHome(event?: Event): void {
    event?.preventDefault();
    void this.router.navigateByUrl('/home');
  }

  goToOsintSearch(event?: Event): void {
    event?.preventDefault();
    void this.router.navigateByUrl('/osint-number-search');
  }

  onMobileSidebarToggle(): void {
    if (window.innerWidth > 991) {
      return;
    }
    window.dispatchEvent(new CustomEvent('mobile-sidebar-toggle'));
  }

  onMobileThemeToggle(): void {
    const nextTheme =
      this.themeService.currentTheme.value === 'dark-theme'
        ? 'light-theme'
        : 'dark-theme';
    this.themeService.setTheme(nextTheme);
    this.themeService.applyTheme(nextTheme);
  }
}
